import { useState } from "react";
import { Bouton } from "@/composants/ui/bouton";
import { Champ, ChampSelection, ChampTexte } from "@/composants/ui/champ";
import { Chargement } from "@/composants/ui/etats";
import { useChargement } from "@/lib/chargement";
import { nomPersonne } from "@/lib/libelles";
import { api, type Collegue, type Personne } from "@/lib/api";

/**
 * Confier la commande à un autre chargé d'affaires (ADR-0032), pour toujours ou
 * jusqu'à une date de fin : elle revient alors à son propriétaire d'avant
 * (ADR-0043).
 *
 * Le bouton qui l'ouvre n'apparaît que si le serveur dit `transferable` ;
 * masquer n'est pas autoriser, et le point d'API refuse quoi qu'il en soit
 * (`TRANSFERER_COMMANDE`).
 */
export function FormulaireTransfert({
  proprietaireActuel,
  onValider,
  onAnnuler,
}: {
  proprietaireActuel: Personne;
  onValider: (proprietaireId: string, fin: string | null, motif: string | null) => Promise<void>;
  onAnnuler: () => void;
}) {
  const collegues = useChargement<Collegue[]>(
    (signal) => api.proprietaires(signal),
    [],
  );
  const [proprietaireId, setProprietaireId] = useState("");
  const [fin, setFin] = useState("");
  const [motif, setMotif] = useState("");
  const [envoi, setEnvoi] = useState(false);

  return (
    <form
      className="mt-4 flex flex-col gap-4 border-t border-filet pt-4"
      onSubmit={async (evenement) => {
        evenement.preventDefault();
        if (!proprietaireId) return;
        setEnvoi(true);
        try {
          await onValider(proprietaireId, fin || null, motif.trim() || null);
        } finally {
          setEnvoi(false);
        }
      }}
    >
      {collegues.enCours ? (
        <Chargement message="Chargement des chargés d'affaires…" />
      ) : (
        <ChampSelection
          libelle="Nouveau propriétaire"
          required
          options={[
            { valeur: "", libelle: "— Choisir —" },
            ...(collegues.donnees ?? [])
              .filter((collegue) => collegue.id !== proprietaireActuel.id)
              .map((collegue) => ({
                valeur: collegue.id,
                libelle: nomPersonne(collegue),
              })),
          ]}
          value={proprietaireId}
          onChange={(e) => setProprietaireId(e.target.value)}
        />
      )}
      <Champ
        libelle="Jusqu'au"
        type="date"
        aide={
          `Vide : définitif. Sinon, la commande revient à ${nomPersonne(proprietaireActuel)}` +
          " le lendemain."
        }
        value={fin}
        onChange={(e) => setFin(e.target.value)}
      />
      <ChampTexte
        libelle="Motif (facultatif)"
        value={motif}
        maxLength={1000}
        onChange={(e) => setMotif(e.target.value)}
      />
      <p className="text-xs text-encre-douce">
        Le vendeur d'origine ne change jamais. L'ancien propriétaire ne garde aucun droit sur la
        commande, même pendant un transfert temporaire ; le transfert est historisé avec son
        auteur, sa date et son motif.
      </p>
      <div className="flex flex-wrap gap-2">
        <Bouton type="submit" variante="principal" disabled={envoi || !proprietaireId}>
          {envoi ? "Transfert…" : "Confier la commande"}
        </Bouton>
        <Bouton type="button" variante="discret" onClick={onAnnuler} disabled={envoi}>
          Annuler
        </Bouton>
      </div>
    </form>
  );
}

/**
 * La fin d'un transfert temporaire : la prolonger, l'abréger, ou la retirer
 * pour le rendre définitif (ADR-0043). Réservé, comme le transfert, à qui peut
 * transférer ; le serveur refuse quoi qu'il en soit.
 */
export function FormulaireFinDuTransfert({
  fin: finActuelle,
  onValider,
  onAnnuler,
}: {
  fin: string;
  onValider: (fin: string | null, motif: string | null) => Promise<void>;
  onAnnuler: () => void;
}) {
  const [fin, setFin] = useState(finActuelle);
  const [motif, setMotif] = useState("");
  const [envoi, setEnvoi] = useState(false);

  return (
    <form
      className="mt-2 flex flex-col gap-4 border-t border-filet pt-4"
      onSubmit={async (evenement) => {
        evenement.preventDefault();
        setEnvoi(true);
        try {
          await onValider(fin || null, motif.trim() || null);
        } finally {
          setEnvoi(false);
        }
      }}
    >
      <Champ
        libelle="Jusqu'au"
        type="date"
        aide="Vide : le transfert devient définitif, la commande ne revient plus."
        value={fin}
        onChange={(e) => setFin(e.target.value)}
      />
      <Champ
        libelle="Motif de la modification (facultatif)"
        value={motif}
        maxLength={1000}
        onChange={(e) => setMotif(e.target.value)}
      />
      <div className="flex flex-wrap gap-2">
        <Bouton type="submit" variante="principal" disabled={envoi}>
          {envoi ? "Enregistrement…" : "Enregistrer la date de fin"}
        </Bouton>
        <Bouton type="button" variante="discret" onClick={onAnnuler} disabled={envoi}>
          Annuler
        </Bouton>
      </div>
    </form>
  );
}

/**
 * « Garder chez le chargé d'affaires actuel » : le transfert pour un temps
 * devient définitif, sa fin et son avertissement s'effacent (décision 76).
 * Le motif est facultatif et s'écrit au journal, comme celui des autres gestes.
 */
export function FormulaireMaintienDuTransfert({
  onValider,
  onAnnuler,
}: {
  onValider: (motif: string | null) => Promise<void>;
  onAnnuler: () => void;
}) {
  const [motif, setMotif] = useState("");
  const [envoi, setEnvoi] = useState(false);

  return (
    <form
      className="mt-2 flex flex-col gap-4 border-t border-filet pt-4"
      onSubmit={async (evenement) => {
        evenement.preventDefault();
        setEnvoi(true);
        try {
          await onValider(motif.trim() || null);
        } finally {
          setEnvoi(false);
        }
      }}
    >
      <Champ
        libelle="Motif du maintien (facultatif)"
        aide="La commande reste chez son propriétaire actuel : la date de fin et l'avertissement disparaissent."
        value={motif}
        maxLength={1000}
        onChange={(e) => setMotif(e.target.value)}
      />
      <div className="flex flex-wrap gap-2">
        <Bouton type="submit" variante="principal" disabled={envoi}>
          {envoi ? "Enregistrement…" : "Confirmer le maintien"}
        </Bouton>
        <Bouton type="button" variante="discret" onClick={onAnnuler} disabled={envoi}>
          Annuler
        </Bouton>
      </div>
    </form>
  );
}
